import { OptimizerProgress, useCalculatorInputOptions } from '@copmer/calculator-integration/client';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CommodityOption } from '@/actions/products/types';
import { useCalculatorPorts } from '@/hooks/use-calculator-ports';
import { getBasePath } from '@/lib/base-path';

import { useCalculatorContextProvideValue } from './context';

export const useCalculatorHook = (
    isCFROptimizer?: boolean,
    onOptimizerProgress?: (progress: OptimizerProgress | null) => void
) => {
    const contextValue = useCalculatorContextProvideValue(isCFROptimizer, onOptimizerProgress);

    const {
        isApiLoaded: coreApiLoaded,
        apiErrorMessage,
        apiLoadingProgress,

        terms,
        vesselMap,
        waterTypes,
    } = useCalculatorInputOptions(contextValue.apiRoutes, null, false);

    const { data: ports, isLoading: portsLoading } = useCalculatorPorts();

    const portMap = useMemo(() => {
        const map: Record<string, string> = {};

        for (const port of ports) {
            map[port.unlocode] = port.displayName;
        }

        return map;
    }, [ports]);

    const commoditiesUrl = `${getBasePath()}/app-api/products/calculator-commodities`;
    const { data: commodities, isLoading: commoditiesLoading } = useQuery<CommodityOption[]>({
        queryKey: ['calculator-commodities', commoditiesUrl],
        queryFn: async () => {
            const response = await fetch(commoditiesUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch commodities: ${response.statusText}`);
            }
            return response.json();
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    const isApiLoaded = coreApiLoaded && !commoditiesLoading && !portsLoading;

    return useMemo(() => {
        return {
            contextValue,
            isApiLoaded,
            terms,
            ports,
            portMap,
            vesselMap,
            commodities,
            apiErrorMessage,
            apiLoadingProgress,
            waterTypes,
        };
    }, [
        apiErrorMessage,
        isApiLoaded,
        contextValue,
        terms,
        ports,
        portMap,
        vesselMap,
        commodities,
        apiLoadingProgress,
        waterTypes,
    ]);
};
